import { Component, Input, SimpleChanges, ViewChild } from '@angular/core';
import { HighchartsChartModule } from 'highcharts-angular';
import * as Highcharts from 'highcharts';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MemberDashboardService } from '../../member-dashboard.service';
import moment from 'moment';

@Component({
  selector: 'app-nutrition',
  standalone: true,
  imports: [CommonModule, FormsModule, HighchartsChartModule, MatProgressSpinnerModule, MatProgressBarModule],
  templateUrl: './nutrition.component.html',
  styleUrl: './nutrition.component.css'
})
export class NutritionComponent {
  @Input() memberId: string = '';
  @Input() startDate: string = '';
  @Input() endDate: string = '';
  @ViewChild('chart1') chart1: any;
  @ViewChild('chart2') chart2: any;
  @ViewChild('chart3') chart3: any;
  @ViewChild('chart4') chart4: any;

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions1: Highcharts.Options = {};
  chartOptions2: Highcharts.Options = {};
  chartOptions3: Highcharts.Options = {};
  chartOptions4: Highcharts.Options = {};
  private chartInstance1: any;
  private chartInstance2: any;
  private chartInstance3: any;
  private chartInstance4: any;
  updateFlag: boolean = false;
  oneToOneFlag: boolean = true;
  runOutsideAngular: boolean = false;
  showChart1: boolean = true;
  showChart2: boolean = true;
  showChart3: boolean = true;
  showChart4: boolean = true;
  isLoading: number = 1;
  noDataFound: boolean = false;
  /** When true, graphs and cards (macros, percentage, eating pattern) are visible; toggle via dropdown arrow */
  nutritionDetailsExpanded: boolean = false;

  // Data arrays
  dates: string[] = [];
  rawDates: string[] = [];
  caloriesTotal: (number | null)[] = [];
  caloriesGoal: (number | null)[] = [];
  caloriesPercentage: (number | null)[] = [];
  proteinTotal: (number | null)[] = [];
  proteinGoal: (number | null)[] = [];
  proteinPercentage: (number | null)[] = [];
  carbsTotal: (number | null)[] = [];
  carbsGoal: (number | null)[] = [];
  carbsPercentage: (number | null)[] = [];
  fatsTotal: (number | null)[] = [];
  fatsGoal: (number | null)[] = [];
  fatsPercentage: (number | null)[] = [];
  breakfastTotal: (number | null)[] = [];
  breakfastGoal: (number | null)[] = [];
  breakfastPercentage: (number | null)[] = [];
  lunchTotal: (number | null)[] = [];
  lunchGoal: (number | null)[] = [];
  lunchPercentage: (number | null)[] = [];
  dinnerTotal: (number | null)[] = [];
  dinnerGoal: (number | null)[] = [];
  dinnerPercentage: (number | null)[] = [];
  snacksTotal: (number | null)[] = [];
  snacksGoal: (number | null)[] = [];
  snacksPercentage: (number | null)[] = [];

  // Summary data from API response (for cards)
  summaryData: {
    calories?: { percentage: number | null; average: number | null; goal: number | null } | null;
    protein?: { percentage: number | null; average: number | null; goal: number | null } | null;
    carbs?: { percentage: number | null; average: number | null; goal: number | null } | null;
    fats?: { percentage: number | null; average: number | null; goal: number | null } | null;
    breakfast?: { percentage: number | null; average: number | null; goal: number | null } | null;
    lunch?: { percentage: number | null; average: number | null; goal: number | null } | null;
    dinner?: { percentage: number | null; average: number | null; goal: number | null } | null;
    snacks?: { percentage: number | null; average: number | null; goal: number | null } | null;
  } = {};

  // Checkbox states for Calories Macros
  selectedMacros: { [key: string]: boolean } = {
    'calories': true,
    'protein': false,
    'carbs': false,
    'fats': false
  };

  // Checkbox states for Eating Pattern
  selectedMeals: { [key: string]: boolean } = {
    'breakfast': true,
    'lunch': false,
    'dinner': false,
    'snacks': false
  };

  constructor(private dashboardService: MemberDashboardService) {}

  toggleNutritionDetails(): void {
    this.nutritionDetailsExpanded = !this.nutritionDetailsExpanded;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['memberId'] || changes['startDate'] || changes['endDate']) && 
        this.memberId && this.startDate && this.endDate) {
      this.isLoading = 1;
      this.getNutritionData();
    }
  }

  getNutritionData(): void {
    this.dashboardService.getLifeStyle(this.memberId, this.startDate, this.endDate).subscribe((response: any) => {
      if (response && response.result && response.result.days) {
        // Store summary data from API response
        if (response.result.calories) {
          this.summaryData.calories = {
            percentage: response.result.calories.percentage ?? null,
            average: response.result.calories.average ?? null,
            goal: response.result.calories.goal ?? null
          };
        } else {
          this.summaryData.calories = null;
        }
        
        if (response.result.protein) {
          this.summaryData.protein = {
            percentage: response.result.protein.percentage ?? null,
            average: response.result.protein.average ?? null,
            goal: response.result.protein.goal ?? null
          };
        } else {
          this.summaryData.protein = null;
        }
        
        if (response.result.carbs) {
          this.summaryData.carbs = {
            percentage: response.result.carbs.percentage ?? null,
            average: response.result.carbs.average ?? null,
            goal: response.result.carbs.goal ?? null
          };
        } else {
          this.summaryData.carbs = null;
        }
        
        if (response.result.fats) {
          this.summaryData.fats = {
            percentage: response.result.fats.percentage ?? null,
            average: response.result.fats.average ?? null,
            goal: response.result.fats.goal ?? null
          };
        } else {
          this.summaryData.fats = null;
        }
        
        if (response.result.breakfast) {
          this.summaryData.breakfast = {
            percentage: response.result.breakfast.percentage ?? null,
            average: response.result.breakfast.average ?? null,
            goal: response.result.breakfast.goal ?? null
          };
        } else {
          this.summaryData.breakfast = null;
        }
        
        if (response.result.lunch) {
          this.summaryData.lunch = {
            percentage: response.result.lunch.percentage ?? null,
            average: response.result.lunch.average ?? null,
            goal: response.result.lunch.goal ?? null
          };
        } else {
          this.summaryData.lunch = null;
        }
        
        if (response.result.dinner) {
          this.summaryData.dinner = {
            percentage: response.result.dinner.percentage ?? null,
            average: response.result.dinner.average ?? null,
            goal: response.result.dinner.goal ?? null
          };
        } else {
          this.summaryData.dinner = null;
        }
        
        if (response.result.snacks) {
          this.summaryData.snacks = {
            percentage: response.result.snacks.percentage ?? null,
            average: response.result.snacks.average ?? null,
            goal: response.result.snacks.goal ?? null
          };
        } else {
          this.summaryData.snacks = null;
        }
        
        this.processNutritionData(response.result.days);
        this.forceChartRecreation();
        setTimeout(() => {
          this.createCharts();
        }, 50);
      } else {
        this.noDataFound = true;
        this.isLoading = 2;
      }
    }, error => {
      this.noDataFound = true;
      this.isLoading = 2;
    });
  }

  processNutritionData(days: any[]): void {
    // Sort days by date
    days.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    this.rawDates = days.map(day => moment(day.date).format('YYYY-MM-DD'));
    this.dates = days.map(day => moment(day.date).format('ddd,<br/>MMM D'));
    
    // Process macros data
    this.caloriesTotal = days.map(day => day.calories?.total ?? null);
    this.caloriesGoal = days.map(day => day.calories?.goal ?? null);
    this.caloriesPercentage = days.map(day => day.calories?.percentage ?? null);
    this.proteinTotal = days.map(day => day.protein?.total ?? null);
    this.proteinGoal = days.map(day => day.protein?.goal ?? null);
    this.proteinPercentage = days.map(day => day.protein?.percentage ?? null);
    this.carbsTotal = days.map(day => day.carbs?.total ?? null);
    this.carbsGoal = days.map(day => day.carbs?.goal ?? null);
    this.carbsPercentage = days.map(day => day.carbs?.percentage ?? null);
    this.fatsTotal = days.map(day => day.fats?.total ?? null);
    this.fatsGoal = days.map(day => day.fats?.goal ?? null);
    this.fatsPercentage = days.map(day => day.fats?.percentage ?? null);

    // Process eating pattern data
    this.breakfastTotal = days.map(day => day.breakfast?.total ?? null);
    this.breakfastGoal = days.map(day => day.breakfast?.goal ?? null);
    this.breakfastPercentage = days.map(day => day.breakfast?.percentage ?? null);
    this.lunchTotal = days.map(day => day.lunch?.total ?? null);
    this.lunchGoal = days.map(day => day.lunch?.goal ?? null);
    this.lunchPercentage = days.map(day => day.lunch?.percentage ?? null);
    this.dinnerTotal = days.map(day => day.dinner?.total ?? null);
    this.dinnerGoal = days.map(day => day.dinner?.goal ?? null);
    this.dinnerPercentage = days.map(day => day.dinner?.percentage ?? null);
    this.snacksTotal = days.map(day => day.snacks?.total ?? null);
    this.snacksGoal = days.map(day => day.snacks?.goal ?? null);
    this.snacksPercentage = days.map(day => day.snacks?.percentage ?? null);

    this.noDataFound = days.length === 0;
  }

  // Get percentage, average, and goal for macros from API summary data
  getCaloriesPercentage(): number | null {
    return this.summaryData.calories?.percentage ?? null;
  }

  getCaloriesAverage(): number | null {
    return this.summaryData.calories?.average ?? null;
  }

  getCaloriesGoal(): number | null {
    return this.summaryData.calories?.goal ?? null;
  }

  getProteinPercentage(): number | null {
    return this.summaryData.protein?.percentage ?? null;
  }

  getProteinAverage(): number | null {
    return this.summaryData.protein?.average ?? null;
  }

  getProteinGoal(): number | null {
    return this.summaryData.protein?.goal ?? null;
  }

  getCarbsPercentage(): number | null {
    return this.summaryData.carbs?.percentage ?? null;
  }

  getCarbsAverage(): number | null {
    return this.summaryData.carbs?.average ?? null;
  }

  getCarbsGoal(): number | null {
    return this.summaryData.carbs?.goal ?? null;
  }

  getFatsPercentage(): number | null {
    return this.summaryData.fats?.percentage ?? null;
  }

  getFatsAverage(): number | null {
    return this.summaryData.fats?.average ?? null;
  }

  getFatsGoal(): number | null {
    return this.summaryData.fats?.goal ?? null;
  }

  // Get percentage, average, and goal for meals from API summary data
  getBreakfastPercentage(): number | null {
    return this.summaryData.breakfast?.percentage ?? null;
  }

  getBreakfastAverage(): number | null {
    return this.summaryData.breakfast?.average ?? null;
  }

  getBreakfastGoal(): number | null {
    return this.summaryData.breakfast?.goal ?? null;
  }

  getLunchPercentage(): number | null {
    return this.summaryData.lunch?.percentage ?? null;
  }

  getLunchAverage(): number | null {
    return this.summaryData.lunch?.average ?? null;
  }

  getLunchGoal(): number | null {
    return this.summaryData.lunch?.goal ?? null;
  }

  getDinnerPercentage(): number | null {
    return this.summaryData.dinner?.percentage ?? null;
  }

  getDinnerAverage(): number | null {
    return this.summaryData.dinner?.average ?? null;
  }

  getDinnerGoal(): number | null {
    return this.summaryData.dinner?.goal ?? null;
  }

  getSnacksPercentage(): number | null {
    return this.summaryData.snacks?.percentage ?? null;
  }

  getSnacksAverage(): number | null {
    return this.summaryData.snacks?.average ?? null;
  }

  getSnacksGoal(): number | null {
    return this.summaryData.snacks?.goal ?? null;
  }

  onMacroToggle(macro: string): void {
    this.isLoading = 1;
    this.forceChartRecreation();
    setTimeout(() => {
      this.createMacrosChart();
      this.createMacrosPercentageChart();
    }, 150);
  }

  onMealToggle(meal: string): void {
    this.isLoading = 1;
    this.forceChartRecreation();
    setTimeout(() => {
      this.createEatingPatternChart();
      this.createEatingPatternPercentageChart();
    }, 150);
  }

  createCharts(): void {
    this.createMacrosChart();
    this.createMacrosPercentageChart();
    this.createEatingPatternChart();
    this.createEatingPatternPercentageChart();
    this.isLoading = 2;
    // Force resize after charts are created to ensure full width
    setTimeout(() => {
      this.forceChartResize();
    }, 300);
  }

  private forceChartResize(): void {
    if (this.chartInstance1) {
      const container = this.chartInstance1.container;
      const parentContainer = container?.closest('.chart-container');
      if (parentContainer) {
        const containerWidth = parentContainer.offsetWidth;
        if (containerWidth > 0) {
          this.chartInstance1.setSize(containerWidth, 400, false);
        }
      }
    }
    if (this.chartInstance2) {
      const container = this.chartInstance2.container;
      const parentContainer = container?.closest('.chart-container');
      if (parentContainer) {
        const containerWidth = parentContainer.offsetWidth;
        if (containerWidth > 0) {
          this.chartInstance2.setSize(containerWidth, 400, false);
        }
      }
    }
    if (this.chartInstance3) {
      const container = this.chartInstance3.container;
      const parentContainer = container?.closest('.chart-container');
      if (parentContainer) {
        const containerWidth = parentContainer.offsetWidth;
        if (containerWidth > 0) {
          this.chartInstance3.setSize(containerWidth, 400, false);
        }
      }
    }
    if (this.chartInstance4) {
      const container = this.chartInstance4.container;
      const parentContainer = container?.closest('.chart-container');
      if (parentContainer) {
        const containerWidth = parentContainer.offsetWidth;
        if (containerWidth > 0) {
          this.chartInstance4.setSize(containerWidth, 400, false);
        }
      }
    }
  }

  createMacrosChart(): void {
    // Check if any macros are selected
    const hasSelectedMacros = Object.values(this.selectedMacros).some(selected => selected);
    
    // Only include data from selected macros for max value calculation
    const selectedData: (number | null)[] = [];
    
    if (this.selectedMacros['calories']) {
      selectedData.push(...this.caloriesTotal, ...this.caloriesGoal);
    }
    if (this.selectedMacros['protein']) {
      selectedData.push(...this.proteinTotal, ...this.proteinGoal);
    }
    if (this.selectedMacros['carbs']) {
      selectedData.push(...this.carbsTotal, ...this.carbsGoal);
    }
    if (this.selectedMacros['fats']) {
      selectedData.push(...this.fatsTotal, ...this.fatsGoal);
    }
    
    const maxValue = this.getMaxValue(selectedData);

    const hasData = maxValue !== undefined && maxValue > 0;

    // Calculate x-axis label step based on number of days (similar to sleep component)
    const totalPoints = this.dates.length;
    const isLongRange = totalPoints > 14;
    const xLabelStep = isLongRange ? Math.ceil(totalPoints / 14) : 1; // target ~14 labels max

    const series: Highcharts.SeriesOptionsType[] = [];

    if (this.selectedMacros['calories']) {
      series.push({
        type: 'line',
        name: 'Calories Intake',
        data: this.caloriesTotal.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#FF3366'
      });
      series.push({
        type: 'line',
        name: 'Calories Goal',
        data: this.caloriesGoal.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#006BF2'
      });
    }

    if (this.selectedMacros['protein']) {
      series.push({
        type: 'line',
        name: 'Protein Intake',
        data: this.proteinTotal.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#009110'
      });
      series.push({
        type: 'line',
        name: 'Protein Goal',
        data: this.proteinGoal.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#09FBD3'
      });
    }

    if (this.selectedMacros['carbs']) {
      series.push({
        type: 'line',
        name: 'Carbs Intake',
        data: this.carbsTotal.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#FFBD00'
      });
      series.push({
        type: 'line',
        name: 'Carbs Goal',
        data: this.carbsGoal.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#7543E8'
      });
    }

    if (this.selectedMacros['fats']) {
      series.push({
        type: 'line',
        name: 'Fats Intake',
        data: this.fatsTotal.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#DE30FF'
      });
      series.push({
        type: 'line',
        name: 'Fats Goal',
        data: this.fatsGoal.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#590202'
      });
    }

    this.chartOptions1 = {
      chart: {
        backgroundColor: '#F9F9F9',
        type: 'line',
        width: null,
        height: 400,
        reflow: true,
        panKey: 'shift',
        panning: {
          enabled: true,
          type: 'x'
        },
        zooming: {
          type: 'x'
        }
      },
      title: {
        text: 'Calories Macros'
      },
      subtitle: {
        text: !hasSelectedMacros ? 'Please choose the macros which you want to see' : (hasData ? '' : 'No data recorded for the selected date range'),
        align: 'center',
        verticalAlign: 'middle',
        style: { color: '#888', fontSize: '14px', fontWeight: '500' }
      },
      xAxis: {
        type: 'datetime',
        categories: this.dates,
        lineWidth: 2,
        labels: {
          useHTML: true,
          step: xLabelStep
        },
        minPadding: 0.05,
        maxPadding: 0.05
      },
      yAxis: {
        min: 0,
        max: maxValue,
        lineWidth: 2,
        gridLineWidth: hasData ? 1 : 0,
        minorGridLineWidth: 0,
        labels: { enabled: hasData },
        tickWidth: hasData ? undefined : 0,
        tickLength: hasData ? undefined : 0,
        title: {
          text: 'Value'
        }
      },
      credits: {
        enabled: false
      },
      tooltip: {
        formatter: function () {
          if (this.y === null || this.y === undefined) {
            return false;
          }
          const customData = this.point.options.custom ?? {};
          const dateStr = customData['dateStr'] || '';
          return `<div>
            <span style="font-size: 12px; color: #fefefe;"><b>${this.series.name}</b></span><br>
            <span style="font-size: 12px; color: #fefefe;">Date: ${dateStr}</span><br>
            <span style="font-size: 12px; color: #fefefe;">Value: ${this.y}</span>
          </div>`;
        },
        backgroundColor: 'rgba(0,0,0, 0.6)',
        borderWidth: 1,
        shadow: true,
        style: {
          color: '#fff',
          fontSize: '10pt',
          fontFamily: 'Urbanist, sans-serif'
        }
      },
      plotOptions: {
        line: {
          dataLabels: {
            enabled: false
          },
          enableMouseTracking: true
        },
        series: {
          marker: {
            symbol: 'circle',
            enabled: true,
            radius: 3,
            lineWidth: 1,
            states: {
              hover: {
                enabled: true,
                radius: 5
              }
            }
          }
        }
      },
      series: series
    };

    this.chartOptions1.chart!.events = {
      load: this.chartCallback1
    };

    this.isLoading = 2;
  }

  createEatingPatternChart(): void {
    // Check if any meals are selected
    const hasSelectedMeals = Object.values(this.selectedMeals).some(selected => selected);
    
    // Only include data from selected meals for max value calculation
    const selectedData: (number | null)[] = [];
    
    if (this.selectedMeals['breakfast']) {
      selectedData.push(...this.breakfastTotal, ...this.breakfastGoal);
    }
    if (this.selectedMeals['lunch']) {
      selectedData.push(...this.lunchTotal, ...this.lunchGoal);
    }
    if (this.selectedMeals['dinner']) {
      selectedData.push(...this.dinnerTotal, ...this.dinnerGoal);
    }
    if (this.selectedMeals['snacks']) {
      selectedData.push(...this.snacksTotal, ...this.snacksGoal);
    }
    
    const maxValue = this.getMaxValue(selectedData);

    const hasData = maxValue !== undefined && maxValue > 0;

    // Calculate x-axis label step based on number of days (similar to sleep component)
    const totalPoints = this.dates.length;
    const isLongRange = totalPoints > 14;
    const xLabelStep = isLongRange ? Math.ceil(totalPoints / 14) : 1; // target ~14 labels max

    const series: Highcharts.SeriesOptionsType[] = [];

    if (this.selectedMeals['breakfast']) {
      series.push({
        type: 'line',
        name: 'Breakfast Intake',
        data: this.breakfastTotal.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#FF3366'
      });
      series.push({
        type: 'line',
        name: 'Breakfast Goal',
        data: this.breakfastGoal.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#006BF2'
      });
    }

    if (this.selectedMeals['lunch']) {
      series.push({
        type: 'line',
        name: 'Lunch Intake',
        data: this.lunchTotal.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#009110'
      });
      series.push({
        type: 'line',
        name: 'Lunch Goal',
        data: this.lunchGoal.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#DE0F08'
      });
    }

    if (this.selectedMeals['dinner']) {
      series.push({
        type: 'line',
        name: 'Dinner Intake',
        data: this.dinnerTotal.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#FFBD00'
      });
      series.push({
        type: 'line',
        name: 'Dinner Goal',
        data: this.dinnerGoal.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#7543E8'
      });
    }

    if (this.selectedMeals['snacks']) {
      series.push({
        type: 'line',
        name: 'Snacks Intake',
        data: this.snacksTotal.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#DE30FF'
      });
      series.push({
        type: 'line',
        name: 'Snacks Goal',
        data: this.snacksGoal.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#590202'
      });
    }

    this.chartOptions2 = {
      chart: {
        backgroundColor: '#F9F9F9',
        type: 'line',
        width: null,
        height: 400,
        reflow: true,
        panKey: 'shift',
        panning: {
          enabled: true,
          type: 'x'
        },
        zooming: {
          type: 'x'
        }
      },
      title: {
        text: 'Eating Pattern'
      },
      subtitle: {
        text: !hasSelectedMeals ? 'Please choose the meals which you want to see' : (hasData ? '' : 'No data recorded for the selected date range'),
        align: 'center',
        verticalAlign: 'middle',
        style: { color: '#888', fontSize: '14px', fontWeight: '500' }
      },
      xAxis: {
        type: 'datetime',
        categories: this.dates,
        lineWidth: 2,
        labels: {
          useHTML: true,
          step: xLabelStep
        },
        minPadding: 0.05,
        maxPadding: 0.05
      },
      yAxis: {
        min: 0,
        max: maxValue,
        lineWidth: 2,
        gridLineWidth: (hasData && hasSelectedMeals) ? 1 : 0,
        minorGridLineWidth: 0,
        labels: { enabled: (hasData && hasSelectedMeals) },
        tickWidth: (hasData && hasSelectedMeals) ? undefined : 0,
        tickLength: (hasData && hasSelectedMeals) ? undefined : 0,
        title: {
          text: 'Value'
        }
      },
      credits: {
        enabled: false
      },
      tooltip: {
        formatter: function () {
          if (this.y === null || this.y === undefined) {
            return false;
          }
          const customData = this.point.options.custom ?? {};
          const dateStr = customData['dateStr'] || '';
          return `<div>
            <span style="font-size: 12px; color: #fefefe;"><b>${this.series.name}</b></span><br>
            <span style="font-size: 12px; color: #fefefe;">Date: ${dateStr}</span><br>
            <span style="font-size: 12px; color: #fefefe;">Value: ${this.y}</span>
          </div>`;
        },
        backgroundColor: 'rgba(0,0,0, 0.6)',
        borderWidth: 1,
        shadow: true,
        style: {
          color: '#fff',
          fontSize: '10pt',
          fontFamily: 'Urbanist, sans-serif'
        }
      },
      plotOptions: {
        line: {
          dataLabels: {
            enabled: false
          },
          enableMouseTracking: true
        },
        series: {
          marker: {
            symbol: 'circle',
            enabled: true,
            radius: 3,
            lineWidth: 1,
            states: {
              hover: {
                enabled: true,
                radius: 5
              }
            }
          }
        }
      },
      series: series
    };

    this.chartOptions2.chart!.events = {
      load: this.chartCallback2
    };

    this.isLoading = 2;
  }

  private getMaxValue(values: (number | null)[]): number | undefined {
    const validValues = values.filter(v => v !== null && v !== undefined && !isNaN(v as number)) as number[];
    if (validValues.length === 0) return undefined;
    return Math.ceil(Math.max(...validValues) * 1.1);
  }

  private chartCallback1 = (chart: any) => {
    const actualChart = chart.target;
    this.chartInstance1 = actualChart;
    // Force chart to use full container width
    setTimeout(() => {
      if (actualChart && actualChart.container) {
        const container = actualChart.container;
        const parentContainer = container.closest('.chart-container');
        if (parentContainer) {
          const containerWidth = parentContainer.offsetWidth;
          if (containerWidth > 0) {
            actualChart.setSize(containerWidth, 400, false);
          }
        }
      }
    }, 100);
  }

  private chartCallback2 = (chart: any) => {
    const actualChart = chart.target;
    this.chartInstance2 = actualChart;
    // Force chart to use full container width
    setTimeout(() => {
      if (actualChart && actualChart.container) {
        const container = actualChart.container;
        const parentContainer = container.closest('.chart-container');
        if (parentContainer) {
          const containerWidth = parentContainer.offsetWidth;
          if (containerWidth > 0) {
            actualChart.setSize(containerWidth, 400, false);
          }
        }
      }
    }, 100);
  }

  private forceChartRecreation(): void {
    this.showChart1 = false;
    this.showChart2 = false;
    this.showChart3 = false;
    this.showChart4 = false;
    setTimeout(() => {
      this.showChart1 = true;
      this.showChart2 = true;
      this.showChart3 = true;
      this.showChart4 = true;
    }, 10);
  }

  private chartCallback3 = (chart: any) => {
    const actualChart = chart.target;
    this.chartInstance3 = actualChart;
    // Force chart to use full container width
    setTimeout(() => {
      if (actualChart && actualChart.container) {
        const container = actualChart.container;
        const parentContainer = container.closest('.chart-container');
        if (parentContainer) {
          const containerWidth = parentContainer.offsetWidth;
          if (containerWidth > 0) {
            actualChart.setSize(containerWidth, 400, false);
          }
        }
      }
    }, 100);
  }

  private chartCallback4 = (chart: any) => {
    const actualChart = chart.target;
    this.chartInstance4 = actualChart;
    // Force chart to use full container width
    setTimeout(() => {
      if (actualChart && actualChart.container) {
        const container = actualChart.container;
        const parentContainer = container.closest('.chart-container');
        if (parentContainer) {
          const containerWidth = parentContainer.offsetWidth;
          if (containerWidth > 0) {
            actualChart.setSize(containerWidth, 400, false);
          }
        }
      }
    }, 100);
  }

  private createEatingPatternPercentageChart(): void {
    // Check if any meals are selected
    const hasSelectedMeals = Object.values(this.selectedMeals).some(selected => selected);

    // Only include data from selected meals for max value calculation
    const selectedData: (number | null)[] = [];

    if (this.selectedMeals['breakfast']) {
      selectedData.push(...this.breakfastPercentage);
    }
    if (this.selectedMeals['lunch']) {
      selectedData.push(...this.lunchPercentage);
    }
    if (this.selectedMeals['dinner']) {
      selectedData.push(...this.dinnerPercentage);
    }
    if (this.selectedMeals['snacks']) {
      selectedData.push(...this.snacksPercentage);
    }

    const hasData = selectedData.some(v => v !== null && v !== undefined && !isNaN(Number(v)));
    const maxValue = hasData ? this.getMaxValue(selectedData) : 100;

    // Calculate x-axis label step based on number of days (similar to sleep component)
    const totalPoints = this.dates.length;
    const isLongRange = totalPoints > 14;
    const xLabelStep = isLongRange ? Math.ceil(totalPoints / 14) : 1; // target ~14 labels max

    const series: Highcharts.SeriesOptionsType[] = [];

    if (this.selectedMeals['breakfast']) {
      series.push({
        type: 'line',
        name: 'Breakfast %',
        data: this.breakfastPercentage.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#FF3366'
      });
    }

    if (this.selectedMeals['lunch']) {
      series.push({
        type: 'line',
        name: 'Lunch %',
        data: this.lunchPercentage.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#009110'
      });
    }

    if (this.selectedMeals['dinner']) {
      series.push({
        type: 'line',
        name: 'Dinner %',
        data: this.dinnerPercentage.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#FFBD00'
      });
    }

    if (this.selectedMeals['snacks']) {
      series.push({
        type: 'line',
        name: 'Snacks %',
        data: this.snacksPercentage.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#DE30FF'
      });
    }

    this.chartOptions4 = {
      chart: {
        backgroundColor: '#F9F9F9',
        type: 'line',
        width: null,
        height: 400,
        reflow: true,
        panKey: 'shift',
        panning: {
          enabled: true,
          type: 'x'
        },
        zooming: {
          type: 'x'
        }
      },
      title: {
        text: 'Eating Pattern Percentage'
      },
      subtitle: {
        text: !hasSelectedMeals ? 'Please choose the meals which you want to see' : (hasData ? '' : 'No data recorded for the selected date range'),
        align: 'center',
        verticalAlign: 'middle',
        style: { color: '#888', fontSize: '14px', fontWeight: '500' }
      },
      xAxis: {
        type: 'datetime',
        categories: this.dates,
        lineWidth: 2,
        labels: {
          useHTML: true,
          step: xLabelStep
        },
        minPadding: 0.05,
        maxPadding: 0.05
      },
      yAxis: {
        min: 0,
        max: maxValue,
        lineWidth: 2,
        gridLineWidth: hasData ? 1 : 0,
        minorGridLineWidth: 0,
        labels: { enabled: hasData },
        tickWidth: hasData ? undefined : 0,
        tickLength: hasData ? undefined : 0,
        title: {
          text: 'Percentage (%)'
        }
      },
      credits: {
        enabled: false
      },
      tooltip: {
        formatter: function () {
          if (this.y === null || this.y === undefined) {
            return false;
          }
          const customData = this.point.options.custom ?? {};
          const dateStr = customData['dateStr'] || '';
          return `<div>
            <span style="font-size: 12px; color: #fefefe;"><b>${this.series.name}</b></span><br>
            <span style="font-size: 12px; color: #fefefe;">Date: ${dateStr}</span><br>
            <span style="font-size: 12px; color: #fefefe;">Value: ${this.y}%</span>
          </div>`;
        },
        backgroundColor: 'rgba(0,0,0, 0.6)',
        borderWidth: 1,
        shadow: true,
        style: {
          color: '#fff',
          fontSize: '10pt',
          fontFamily: 'Urbanist, sans-serif'
        }
      },
      plotOptions: {
        line: {
          dataLabels: {
            enabled: false
          },
          enableMouseTracking: true
        },
        series: {
          marker: {
            symbol: 'circle',
            enabled: true,
            radius: 3,
            lineWidth: 1,
            states: {
              hover: {
                enabled: true,
                radius: 5
              }
            }
          }
        }
      },
      series: series
    };

    this.chartOptions4.chart!.events = {
      load: this.chartCallback4
    };

    this.isLoading = 2;
  }

  private createMacrosPercentageChart(): void {
    // Check if any macros are selected
    const hasSelectedMacros = Object.values(this.selectedMacros).some(selected => selected);

    // Only include data from selected macros for max value calculation
    const selectedData: (number | null)[] = [];

    if (this.selectedMacros['calories']) {
      selectedData.push(...this.caloriesPercentage);
    }
    if (this.selectedMacros['protein']) {
      selectedData.push(...this.proteinPercentage);
    }
    if (this.selectedMacros['carbs']) {
      selectedData.push(...this.carbsPercentage);
    }
    if (this.selectedMacros['fats']) {
      selectedData.push(...this.fatsPercentage);
    }

    const hasData = selectedData.some(v => v !== null && v !== undefined && !isNaN(Number(v)));
    const maxValue = hasData ? this.getMaxValue(selectedData) : 100;

    // Calculate x-axis label step based on number of days (similar to sleep component)
    const totalPoints = this.dates.length;
    const isLongRange = totalPoints > 14;
    const xLabelStep = isLongRange ? Math.ceil(totalPoints / 14) : 1; // target ~14 labels max

    const series: Highcharts.SeriesOptionsType[] = [];

    if (this.selectedMacros['calories']) {
      series.push({
        type: 'line',
        name: 'Calories %',
        data: this.caloriesPercentage.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#FF3366'
      });
    }

    if (this.selectedMacros['protein']) {
      series.push({
        type: 'line',
        name: 'Protein %',
        data: this.proteinPercentage.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#009110'
      });
    }

    if (this.selectedMacros['carbs']) {
      series.push({
        type: 'line',
        name: 'Carbs %',
        data: this.carbsPercentage.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#FFBD00'
      });
    }

    if (this.selectedMacros['fats']) {
      series.push({
        type: 'line',
        name: 'Fats %',
        data: this.fatsPercentage.map((value, index) => ({
          x: index,
          y: value,
          custom: {
            dateStr: this.rawDates[index] ? moment(this.rawDates[index]).format('ddd, MMM D, YYYY') : ''
          }
        })) as Highcharts.PointOptionsObject[],
        color: '#DE30FF'
      });
    }

    this.chartOptions3 = {
      chart: {
        backgroundColor: '#F9F9F9',
        type: 'line',
        width: null,
        height: 400,
        reflow: true,
        panKey: 'shift',
        panning: {
          enabled: true,
          type: 'x'
        },
        zooming: {
          type: 'x'
        }
      },
      title: {
        text: 'Calories Macros Percentage'
      },
      subtitle: {
        text: !hasSelectedMacros ? 'Please choose the macros which you want to see' : (hasData ? '' : 'No data recorded for the selected date range'),
        align: 'center',
        verticalAlign: 'middle',
        style: { color: '#888', fontSize: '14px', fontWeight: '500' }
      },
      xAxis: {
        type: 'datetime',
        categories: this.dates,
        lineWidth: 2,
        labels: {
          useHTML: true,
          step: xLabelStep
        },
        minPadding: 0.05,
        maxPadding: 0.05
      },
      yAxis: {
        min: 0,
        max: maxValue,
        lineWidth: 2,
        gridLineWidth: hasData ? 1 : 0,
        minorGridLineWidth: 0,
        labels: { enabled: hasData },
        tickWidth: hasData ? undefined : 0,
        tickLength: hasData ? undefined : 0,
        title: {
          text: 'Percentage (%)'
        }
      },
      credits: {
        enabled: false
      },
      tooltip: {
        formatter: function () {
          if (this.y === null || this.y === undefined) {
            return false;
          }
          const customData = this.point.options.custom ?? {};
          const dateStr = customData['dateStr'] || '';
          return `<div>
            <span style="font-size: 12px; color: #fefefe;"><b>${this.series.name}</b></span><br>
            <span style="font-size: 12px; color: #fefefe;">Date: ${dateStr}</span><br>
            <span style="font-size: 12px; color: #fefefe;">Value: ${this.y}%</span>
          </div>`;
        },
        backgroundColor: 'rgba(0,0,0, 0.6)',
        borderWidth: 1,
        shadow: true,
        style: {
          color: '#fff',
          fontSize: '10pt',
          fontFamily: 'Urbanist, sans-serif'
        }
      },
      plotOptions: {
        line: {
          dataLabels: {
            enabled: false
          },
          enableMouseTracking: true
        },
        series: {
          marker: {
            symbol: 'circle',
            enabled: true,
            radius: 3,
            lineWidth: 1,
            states: {
              hover: {
                enabled: true,
                radius: 5
              }
            }
          }
        }
      },
      series: series
    };

    this.chartOptions3.chart!.events = {
      load: this.chartCallback3
    };

    this.isLoading = 2;
  }
}
